/**
 * 📊 «الأداء» — بتقيس الشغل مش القعدة.
 *
 * ⛔ **الشاشة دي بتقول بصوت عالي إنها مش مراقبة.** السطر اللي فوق مش
 *    تجميل: الموظف اللي مش عارف إيه اللي بيتقاس بيفترض الأسوأ، والافتراض
 *    ده بيخرّب الثقة أكتر من القياس نفسه. اللي بيتقاس مكتوب، واللي مش
 *    بيتقاس مكتوب كمان.
 *
 * 🔑 وترتيب الأرقام مقصود: **المهام المقفولة الأول، والدقايق آخر حاجة.**
 *    اللي فوق هو اللي بيتحاسب عليه.
 */
import { useEffect, useMemo, useState } from "react";

import { errorMessageAr } from "@/api/client";
import {
  fetchMyPerformance,
  fetchTeamPerformance,
  minutesLabelAr,
  type PerfRow,
  type PerfSnapshot,
} from "@/api/performance";
import { ErrorState, LoadingState } from "@/components/states/ScreenStates";
import { Shell } from "@/components/Shell";
import { useSession } from "@/lib/session";
import { Ltr, fmtNum } from "@/lib/format";

const WINDOWS = [
  { days: 1, label: "النهارده" },
  { days: 7, label: "آخر أسبوع" },
  { days: 30, label: "آخر شهر" },
] as const;

export function Performance() {
  const { me } = useSession();
  const perms = useMemo(() => new Set(me?.permissions ?? []), [me]);
  const canSeeTeam = perms.has("user.read");

  const [days, setDays] = useState<number>(7);
  const [mine, setMine] = useState<PerfSnapshot | null>(null);
  const [team, setTeam] = useState<PerfRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const snap = await fetchMyPerformance(days, ctrl.signal);
        setMine(snap);
        if (canSeeTeam) setTeam(await fetchTeamPerformance(days, ctrl.signal));
      } catch (err) {
        if (!ctrl.signal.aborted) setError(errorMessageAr(err));
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [days, canSeeTeam]);

  return (
    <Shell title="الأداء">
      <div className="sec-h" style={{ flexWrap: "wrap" }}>
        <span className="sec-title">أرقامي</span>
        <div className="filters">
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              className={`filt${days === w.days ? " on" : ""}`}
              aria-pressed={days === w.days}
              onClick={() => setDays(w.days)}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* 🔒 الشفافية جزء من التصميم مش حاشية */}
      <div className="card" style={{ padding: 14 }}>
        <span className="t-desc">
          اللي بيتقاس: المهام اللي قفلتها، والوقت من ساعة ما تستلم المهمة لحد ما
          تقفلها، والمتأخر، ودقايق فيها شغل فعلي جوّه البرنامج.
        </span>
        <span className="t-meta" style={{ display: "block", marginTop: 6 }}>
          ⛔ اللي مش بيتقاس ولا هيتقاس: صور شاشة · أي حاجة بره البرنامج ·
          الجهاز مفتوح ولا لأ · اللي بتكتبه أو بتقراه.
        </span>
      </div>

      {loading && !mine ? <LoadingState rows={3} /> : null}
      {error ? <ErrorState messageAr={error} onRetry={() => setDays((d) => d)} /> : null}

      {mine ? <PerfCards snap={mine} /> : null}

      {canSeeTeam && team && team.length > 0 ? (
        <>
          <div className="sec-h">
            <span className="sec-title">الفريق</span>
            <span className="sec-note">مرتّب بالمهام المقفولة</span>
          </div>
          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>الموظف</th>
                  <th style={{ width: 120 }}>قفل</th>
                  <th style={{ width: 130 }}>متوسط الإنجاز</th>
                  <th style={{ width: 110 }}>متأخر</th>
                  <th style={{ width: 150 }}>شغل فعلي</th>
                </tr>
              </thead>
              <tbody>
                {team.map((row) => (
                  <tr key={row.user_id}>
                    <td>
                      <span className="t-body">{row.name_ar}</span>
                    </td>
                    <td>
                      <span className="t-body">{fmtNum(row.outcomes.closed)}</span>
                    </td>
                    <td>
                      <span className="t-desc">{avgLabel(row.outcomes)}</span>
                    </td>
                    <td>
                      {row.outcomes.overdue_now > 0 ? (
                        <span className="chip late">
                          <span className="bullet" />
                          {fmtNum(row.outcomes.overdue_now)}
                        </span>
                      ) : (
                        <span className="t-meta">—</span>
                      )}
                    </td>
                    <td>
                      <span className="t-desc">
                        {minutesLabelAr(row.active_minutes_total)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Shell>
  );
}

/**
 * ⚠️ `null` مش صفر. «مفيش مهمة مقفولة نقيس عليها» غير «بيخلص في صفر دقيقة»
 *    — والتانية كانت هتبان أداءً خارقًا وهي مفيش قياس أصلًا.
 */
function avgLabel(o: PerfSnapshot["outcomes"]): string {
  if (o.avg_claim_to_close_minutes === null) return "لسه مفيش قياس";
  return minutesLabelAr(o.avg_claim_to_close_minutes);
}

function PerfCards({ snap }: { snap: PerfSnapshot }) {
  const o = snap.outcomes;
  return (
    <div className="stats c3">
      <Stat label="مهام قفلتها" value={fmtNum(o.closed)} />
      <Stat label="متوسط من الاستلام للإقفال" value={avgLabel(o)} />
      <Stat label="مفتوحة معايا دلوقتي" value={fmtNum(o.open_now)} />
      <Stat
        label="متأخرة عليّ"
        value={o.overdue_now > 0 ? fmtNum(o.overdue_now) : "مفيش"}
      />
      <Stat label="أفعال النهارده" value={fmtNum(snap.span_today.actions)} />
      <Stat
        label={snap.days_window === 1 ? "شغل فعلي النهارده" : "شغل فعلي في الفترة"}
        value={minutesLabelAr(
          snap.days_window === 1 ? snap.active_minutes_today : snap.active_minutes_total,
        )}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat" style={{ cursor: "default" }}>
      <div className="v">
        <Ltr>{value}</Ltr>
      </div>
      <div className="l">{label}</div>
    </div>
  );
}
